const DB_NAME = "showcan-auth"
const STORE_NAME = "tokens"
const ACCESS_TOKEN_KEY = "accessToken"
const REFRESH_TOKEN_KEY = "refreshToken"
const EXPIRES_AT_KEY = "expiresAt"
const TENANT_ID_KEY = "tenantId"
const IS_OWNER_KEY = "isOwner"
const CHATBOT_ENABLED_KEY = "chatbotEnabled"
const VOICE_AGENT_ENABLED_KEY = "voiceAgentEnabled"
const VOICE_GENERATOR_ENABLED_KEY = "voiceGeneratorEnabled"

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
  })
}

async function getValue<T>(key: string): Promise<T | null> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly")
    const store = transaction.objectStore(STORE_NAME)
    const request = store.get(key)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result ?? null)
  })
}

async function setValue<T>(key: string, value: T): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite")
    const store = transaction.objectStore(STORE_NAME)
    const request = store.put(value, key)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve()
  })
}

async function deleteValue(key: string): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite")
    const store = transaction.objectStore(STORE_NAME)
    const request = store.delete(key)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve()
  })
}

export const tokenStore = {
  async getAccessToken(): Promise<string | null> {
    const expiresAt = await getValue<number>(EXPIRES_AT_KEY)
    if (expiresAt && Date.now() >= expiresAt) {
      return null
    }
    return getValue<string>(ACCESS_TOKEN_KEY)
  },

  async getRefreshToken(): Promise<string | null> {
    return getValue<string>(REFRESH_TOKEN_KEY)
  },

  async setTokens(
    accessToken: string,
    refreshToken: string,
    expiresIn: number
  ): Promise<void> {
    const expiresAt = Date.now() + expiresIn * 1000
    await setValue(ACCESS_TOKEN_KEY, accessToken)
    await setValue(REFRESH_TOKEN_KEY, refreshToken)
    await setValue(EXPIRES_AT_KEY, expiresAt)
  },

  async clearAll(): Promise<void> {
    await deleteValue(ACCESS_TOKEN_KEY)
    await deleteValue(REFRESH_TOKEN_KEY)
    await deleteValue(EXPIRES_AT_KEY)
    await deleteValue(TENANT_ID_KEY)
    await deleteValue(IS_OWNER_KEY)
    await deleteValue(CHATBOT_ENABLED_KEY)
    await deleteValue(VOICE_AGENT_ENABLED_KEY)
    await deleteValue(VOICE_GENERATOR_ENABLED_KEY)
  },

  async getTenantId(): Promise<string | null> {
    return getValue<string>(TENANT_ID_KEY)
  },

  async getIsOwner(): Promise<boolean> {
    const value = await getValue<boolean>(IS_OWNER_KEY)
    return value ?? false
  },

  async getChatbotEnabled(): Promise<boolean> {
    const value = await getValue<boolean>(CHATBOT_ENABLED_KEY)
    return value ?? true
  },

  async getVoiceAgentEnabled(): Promise<boolean> {
    const value = await getValue<boolean>(VOICE_AGENT_ENABLED_KEY)
    return value ?? false
  },

  async getVoiceGeneratorEnabled(): Promise<boolean> {
    const value = await getValue<boolean>(VOICE_GENERATOR_ENABLED_KEY)
    return value ?? false
  },

  async setTenantInfo(
    tenantId: string,
    isOwner: boolean,
    chatbotEnabled: boolean = true,
    voiceAgentEnabled: boolean = false,
    voiceGeneratorEnabled: boolean = false,
  ): Promise<void> {
    await setValue(TENANT_ID_KEY, tenantId)
    await setValue(IS_OWNER_KEY, isOwner)
    await setValue(CHATBOT_ENABLED_KEY, chatbotEnabled)
    await setValue(VOICE_AGENT_ENABLED_KEY, voiceAgentEnabled)
    await setValue(VOICE_GENERATOR_ENABLED_KEY, voiceGeneratorEnabled)
  },
}
